import React, { useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { BLOCK_TYPES, BlockFields, blockLabel, newBlock } from './blockFields';

function InsertBar({ onAdd, index, alwaysOpen = false }) {
  const [open, setOpen] = useState(alwaysOpen);
  if (!open) {
    return (
      <div className="relative h-6 group flex items-center justify-center">
        <div className="absolute inset-x-0 top-1/2 h-px bg-white/5 group-hover:bg-[#00b8ff]/40 transition-colors duration-200" />
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="relative w-6 h-6 rounded-full bg-black border border-white/15 text-[#7a7a7a] flex items-center justify-center hover:border-[#00b8ff] hover:text-[#00b8ff] transition-colors duration-200"
          aria-label="Insert block here"
          data-testid={`block-insert-toggle-${index}`}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 py-2" data-testid={`block-insert-bar-${index}`}>
      {BLOCK_TYPES.map((t) => (
        <button
          type="button"
          key={t.type}
          onClick={() => { onAdd(index, t.type); if (!alwaysOpen) setOpen(false); }}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3.5 py-1.5 text-xs text-[#cfc8bb] hover:border-[#00b8ff] hover:text-[#00b8ff] transition-colors duration-200"
          data-testid={`block-add-${t.type}-${index}`}
        >
          <Plus className="w-3 h-3" />{t.label}
        </button>
      ))}
      {!alwaysOpen && (
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-[#5a5a5a] hover:text-[#cfc8bb] px-2">
          Cancel
        </button>
      )}
    </div>
  );
}

function BlockToolbar({ type, index, count, onMove, onDelete }) {
  const btn = 'w-8 h-8 rounded-full flex items-center justify-center text-[#7a7a7a] hover:text-[#f4efe6] hover:bg-white/5 disabled:opacity-30 transition-colors duration-200';
  return (
    <div className="flex items-center justify-between mb-2">
      <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#00b8ff]">{blockLabel(type)}</span>
      <div className="flex items-center gap-1">
        <button type="button" className={btn} disabled={index === 0} onClick={() => onMove(index, -1)} aria-label="Move up" data-testid={`block-up-${index}`}><ArrowUp className="w-4 h-4" /></button>
        <button type="button" className={btn} disabled={index === count - 1} onClick={() => onMove(index, 1)} aria-label="Move down" data-testid={`block-down-${index}`}><ArrowDown className="w-4 h-4" /></button>
        <button type="button" className={`${btn} hover:text-red-400`} onClick={() => onDelete(index)} aria-label="Delete block" data-testid={`block-delete-${index}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export default function BlockEditor({ blocks, onChange }) {
  const add = (at, type) => onChange([...blocks.slice(0, at), newBlock(type), ...blocks.slice(at)]);
  const update = (i, b) => onChange(blocks.map((x, j) => (j === i ? b : x)));
  const remove = (i) => onChange(blocks.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const next = [...blocks];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    onChange(next);
  };

  return (
    <div data-testid="block-editor">
      <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#00b8ff] mb-3">Article</label>
      <div className="rounded-xl border border-white/10 p-4 md:p-5 bg-white/[0.015]">
        {blocks.map((b, i) => (
          <React.Fragment key={b._key}>
            {i > 0 && <InsertBar index={i} onAdd={add} />}
            <div className="rounded-lg border border-white/5 p-3 hover:border-white/15 transition-colors duration-200" data-testid={`block-${i}`} data-block-type={b.type}>
              <BlockToolbar type={b.type} index={i} count={blocks.length} onMove={move} onDelete={remove} />
              <BlockFields block={b} onChange={(nb) => update(i, nb)} testid={`block-${i}`} />
            </div>
          </React.Fragment>
        ))}
        <div className={blocks.length ? 'mt-4 pt-3 border-t border-white/5' : ''}>
          <InsertBar index={blocks.length} onAdd={add} alwaysOpen />
        </div>
      </div>
    </div>
  );
}
